/* eslint-disable react/no-unescaped-entities */
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Linkedin, Youtube, ExternalLink } from "lucide-react";

// Use a simple X (Twitter) icon since lucide's Twitter is the old bird
const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socials = [
  { icon: Linkedin, href: "https://www.linkedin.com/in/cashivangimittal/", label: "LinkedIn", testid: "social-linkedin" },
  { icon: XLogo, href: "https://x.com/CAShivangi_m", label: "X", testid: "social-twitter" },
  { icon: Youtube, href: "https://www.youtube.com/@CAShivangiMittal", label: "YouTube", testid: "social-youtube" },
];

const heroStats = [
  { v: "15+", l: "Years in Tax" },
  { v: "1800+", l: "Queries on TaxSetu" },
  { v: "1000+", l: "Students Trained" },
];

const heroProducts = [
  {
    name: "TaxSetu.ai",
    desc: "AI Income Tax Research",
    href: "https://www.taxsetu.ai",
    accent: "#16D1C8",
    accent2: "#0EE6D9",
    testid: "hero-product-taxsetu",
  },
  {
    name: "PractiQ",
    desc: "India's first CA Job Simulator",
    href: "https://practiq.taxsetu.ai",
    accent: "#FF2D7A",
    accent2: "#FF8A3D",
    testid: "hero-product-practiq",
  },
];

const goTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section id="hero" data-testid="hero-section" className="relative pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* decorative blobs */}
      <div className="shape-blob w-[520px] h-[520px] -top-32 -left-32 opacity-50"
           style={{ background: "radial-gradient(circle, #16D1C8 0%, transparent 70%)" }} />
      <div className="shape-blob w-[480px] h-[480px] top-40 -right-32 opacity-40"
           style={{ background: "radial-gradient(circle, #FF2D7A 0%, transparent 70%)" }} />
      <div className="shape-blob w-[320px] h-[320px] bottom-0 left-1/3 opacity-30"
           style={{ background: "radial-gradient(circle, #FF8A3D 0%, transparent 70%)" }} />

      {/* subtle grid */}
      <div className="absolute inset-0 subtle-grid opacity-50" />

      {/* arcs */}
      <div className="hidden md:block arc-ring w-[600px] h-[600px] -right-40 top-10 opacity-40" />
      <div className="hidden md:block arc-ring w-[300px] h-[300px] -left-20 bottom-10 opacity-40" />

      {/* pink diagonal stripes accent */}
      <div className="hidden md:block absolute right-10 bottom-20 w-40 h-40 diag-stripes opacity-60 rotate-12" />

      <div className="container-x relative z-10 grid lg:grid-cols-[1.15fr,0.85fr] gap-10 lg:gap-14 items-center">
        {/* LEFT: Text */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 glass !rounded-full px-4 py-2 mb-6"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-teal-400" />
            <span className="text-xs tracking-wider uppercase text-white/80">Practicing CA · Founder · Faculty</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-title"
            className="font-heading font-bold leading-[1.15] pb-2 text-4xl sm:text-5xl lg:text-6xl"
          >
            <span className="block text-white" style={{ lineHeight: 1.2, paddingBottom: '0.1em' }}>Hi, I'm</span>
            <span className="block gradient-text" style={{ lineHeight: 1.25, paddingBottom: '0.2em' }}>Shivangi Mittal</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="hero-tagline"
            className="mt-5 text-base md:text-lg text-white/75 leading-relaxed max-w-2xl"
          >
            Practicing Chartered Accountant with{" "}
            <span className="text-teal-300 font-medium">15+ years in taxation</span> — helping{" "}
            <span className="text-white">Corporates, HNIs and NRIs</span> navigate complex tax
            landscapes, while building <span className="text-white">AI tools</span> that simplify
            the profession for everyone else.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-3 text-sm md:text-base text-white/55 italic max-w-xl"
          >
            "Tax is never just numbers. It is decisions, consequences, and clarity under pressure."
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <button data-testid="hero-cta-connect" onClick={() => goTo("contact")} className="btn-primary">
              Let's Connect <ArrowRight size={18} />
            </button>
            <button data-testid="hero-cta-explore" onClick={() => goTo("pillars")} className="btn-outline">
              Explore My Work
            </button>
          </motion.div>

          {/* Inline stats — key info above the fold */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="hero-stats"
            className="mt-8 grid grid-cols-3 gap-3 max-w-xl"
          >
            {heroStats.map((s, i) => (
              <div
                key={s.l}
                data-testid={`hero-stat-${i}`}
                className="glass !rounded-2xl px-4 py-3"
              >
                <div className="font-heading font-bold text-xl md:text-2xl gradient-text-cool">{s.v}</div>
                <div className="text-[11px] uppercase tracking-wider text-white/55 mt-0.5">{s.l}</div>
              </div>
            ))}
          </motion.div>

          {/* socials */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 flex items-center gap-3"
            data-testid="hero-socials"
          >
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.testid}
                aria-label={s.label}
                className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-white hover:border-teal-400/60 hover:bg-teal-400/5 transition-all"
              >
                <s.icon size={15} />
              </a>
            ))}
          </motion.div>
        </div>

        {/* RIGHT: Portrait with halo + product cards below */}
        <div className="flex flex-col items-center gap-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="relative mx-auto w-[260px] sm:w-[320px] md:w-[380px] aspect-square"
            data-testid="hero-portrait"
          >
            {/* outer halo */}
            <div className="absolute inset-0 rounded-full animate-pulse-glow"
                 style={{ background: "conic-gradient(from 0deg, #16D1C8, #FF2D7A, #FF8A3D, #16D1C8)", filter: "blur(40px)", opacity: 0.6 }} />
            {/* circular ring */}
            <div className="absolute inset-6 rounded-full"
                 style={{ background: "conic-gradient(from 220deg, #16D1C8 0%, #0EE6D9 30%, transparent 50%, transparent 80%, #FF2D7A 100%)" }} />
            {/* inner dark circle */}
            <div className="absolute inset-10 rounded-full bg-ink-800" />
            {/* portrait */}
            <div className="absolute inset-12 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_shivangi-digital/artifacts/pijfan6p_PicShivangiwithwhitebg.jpeg.png"
                alt="Shivangi Mittal"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* floating accent shapes */}
            <div className="absolute -top-4 -right-2 w-16 h-16 rounded-full opacity-90 animate-float-slow"
                 style={{ background: "linear-gradient(135deg, #FF2D7A, #FF8A3D)" }} />
            <div className="absolute -bottom-3 -left-3 w-12 h-12 rounded-full opacity-80 animate-float-slow"
                 style={{ background: "linear-gradient(135deg, #16D1C8, #0EE6D9)", animationDelay: "2s" }} />
          </motion.div>

          {/* Product mini-cards — TaxSetu.ai & PractiQ above the fold */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="hero-products"
            className="w-full max-w-md grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {heroProducts.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={p.testid}
                className="group glass !rounded-2xl p-4 hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-25 group-hover:opacity-60 blur-2xl transition-opacity"
                     style={{ background: p.accent }} />
                <div className="relative z-10 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-white text-base truncate">{p.name}</div>
                    <div className="text-[11px] text-white/55 mt-0.5">{p.desc}</div>
                  </div>
                  <ExternalLink size={14} className="shrink-0 text-white/40 group-hover:text-teal-300 transition-colors" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-px opacity-40 group-hover:opacity-100 transition-opacity"
                     style={{ background: `linear-gradient(90deg, transparent, ${p.accent}, ${p.accent2}, transparent)` }} />
              </a>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
